import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faX, faSearch, faTrash, faToggleOn, faToggleOff, faSave, faStore
} from '@fortawesome/free-solid-svg-icons';
import { Loader } from '../Loader/Loader';
import { Msg200or201, Msg500 } from '../Messages/Messages';
import Pagination, { usePagination } from '../Components/Pagination';
import { decryptData } from '../../utils/storageCrypto';

// ---------------------------------------------------------------------------
// Modal/Screen: HubAssignedProductsManager
// Purpose: View everything already assigned to a single hub and manage it in
// bulk — activate/deactivate, remove, or edit price & stock per row.
//
// Backend endpoints assumed (adjust to match actual API contracts):
//   GET  hub-product/get-assigned-products?hubId=
//   PUT  hub-product/update-assignment           (single row price/stock/sku)
//   POST hub-product/bulk-update-status          { hub_id, product_ids, is_active }
//   POST hub-product/bulk-remove                 { hub_id, product_ids }
// ---------------------------------------------------------------------------

const HubAssignedProductsManager = ({ hub, onClose }) => {
  const fetchUrl = process.env.REACT_APP_FORE_BRICKBUCKET;
  const imageUrl = process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES || '';

  const [assignments, setAssignments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState({});
  const [editRows, setEditRows] = useState({}); // productId -> { price, stock }
  const [popup, setPopup] = useState({ type: '', message: '' });
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  const getAuthHeaders = () => {
    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
    return { 'Content-Type': 'application/json', 'x-token': token };
  };

  useEffect(() => {
    fetchAssignedProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hub._id]);

  const fetchAssignedProducts = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${fetchUrl}hub-product/get-assigned-products?hubId=${hub._id}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (response.ok) {
        setAssignments(data.data || []);
      } else {
        setPopup({ type: '500', message: data.message || 'Failed to load assigned products.' });
      }
    } catch (error) {
      console.error('Failed to fetch assigned products:', error);
      setPopup({ type: '500', message: 'Failed to load assigned products.' });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredAssignments = useMemo(() => {
    const term = searchTerm.toLowerCase();
    if (!term) return assignments;
    return assignments.filter(a =>
      a.product_name?.toLowerCase().includes(term) || a.sku?.toLowerCase().includes(term)
    );
  }, [assignments, searchTerm]);

  const {
    currentItems, currentPage, totalPages, totalItems, itemsPerPage,
    handlePageChange, handlePreviousPage, handleNextPage, resetToFirstPage,
  } = usePagination(filteredAssignments, 10);

  useEffect(() => { resetToFirstPage(); }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedCount = Object.values(selectedIds).filter(Boolean).length;
  const allVisibleSelected = currentItems.length > 0 && currentItems.every(a => selectedIds[a.product_id]);

  const toggleRow = (productId) => {
    setSelectedIds(prev => ({ ...prev, [productId]: !prev[productId] }));
  };

  const toggleSelectAllVisible = () => {
    const next = { ...selectedIds };
    currentItems.forEach(a => { next[a.product_id] = !allVisibleSelected; });
    setSelectedIds(next);
  };

  const startEdit = (assignment) => {
    setEditRows(prev => ({
      ...prev,
      [assignment.product_id]: { price: assignment.price, stock: assignment.stock ?? '' }
    }));
  };

  const updateEditField = (productId, field, value) => {
    setEditRows(prev => ({ ...prev, [productId]: { ...prev[productId], [field]: value } }));
  };

  const saveRow = async (assignment) => {
    const edit = editRows[assignment.product_id];
    if (!edit) return;
    setIsBusy(true);
    try {
      const response = await fetch(`${fetchUrl}hub-product/update-assignment`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          hub_id: hub._id,
          product_id: assignment.product_id,
          price: parseFloat(edit.price) || 0,
          stock: edit.stock !== '' ? parseInt(edit.stock) || 0 : null,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setAssignments(prev => prev.map(a =>
          a.product_id === assignment.product_id ? { ...a, price: parseFloat(edit.price) || 0, stock: edit.stock } : a
        ));
        setEditRows(prev => {
          const next = { ...prev };
          delete next[assignment.product_id];
          return next;
        });
        setPopup({ type: '200', message: 'Updated successfully.' });
        setTimeout(() => setPopup({ type: '', message: '' }), 2000);
      } else {
        setPopup({ type: '500', message: data.message || 'Failed to update.' });
      }
    } catch (error) {
      console.error('Failed to update assignment:', error);
      setPopup({ type: '500', message: 'Failed to update. Please try again.' });
    } finally {
      setIsBusy(false);
    }
  };

  const bulkUpdateStatus = async (isActive) => {
    const productIds = Object.entries(selectedIds).filter(([, v]) => v).map(([id]) => id);
    if (productIds.length === 0) return;
    setIsBusy(true);
    try {
      const response = await fetch(`${fetchUrl}hub-product/bulk-update-status`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ hub_id: hub._id, product_ids: productIds, is_active: isActive }),
      });
      const data = await response.json();
      if (response.ok) {
        setAssignments(prev => prev.map(a =>
          productIds.includes(a.product_id) ? { ...a, is_active: isActive } : a
        ));
        setSelectedIds({});
        setPopup({ type: '200', message: `${productIds.length} product(s) ${isActive ? 'activated' : 'deactivated'}.` });
        setTimeout(() => setPopup({ type: '', message: '' }), 2000);
      } else {
        setPopup({ type: '500', message: data.message || 'Bulk update failed.' });
      }
    } catch (error) {
      console.error('Bulk status update failed:', error);
      setPopup({ type: '500', message: 'Bulk update failed. Please try again.' });
    } finally {
      setIsBusy(false);
    }
  };

  const bulkRemove = async () => {
    const productIds = Object.entries(selectedIds).filter(([, v]) => v).map(([id]) => id);
    if (productIds.length === 0) return;
    setIsBusy(true);
    try {
      const response = await fetch(`${fetchUrl}hub-product/bulk-remove`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ hub_id: hub._id, product_ids: productIds }),
      });
      const data = await response.json();
      if (response.ok) {
        setAssignments(prev => prev.filter(a => !productIds.includes(a.product_id)));
        setSelectedIds({});
        setPopup({ type: '200', message: `${productIds.length} product(s) removed from hub.` });
        setTimeout(() => setPopup({ type: '', message: '' }), 2000);
      } else {
        setPopup({ type: '500', message: data.message || 'Bulk remove failed.' });
      }
    } catch (error) {
      console.error('Bulk remove failed:', error);
      setPopup({ type: '500', message: 'Bulk remove failed. Please try again.' });
    } finally {
      setIsBusy(false);
      setConfirmRemove(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      {popup.type === '200' && <Msg200or201 message={popup.message} />}
      {popup.type === '500' && <Msg500 message={popup.message} closePopup={() => setPopup({ type: '', message: '' })} />}

      <div className="bg-white rounded-lg shadow-lg w-full max-w-6xl h-[95vh] sm:h-[90vh] flex flex-col">

        {/* Header */}
        <div className="border-b px-3 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center gap-2">
              <FontAwesomeIcon icon={faStore} className="text-[#F7525A]" />
              {hub.hub_name}
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">Hub #{hub.hub_number} — Assigned Products</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 flex-shrink-0">
            <FontAwesomeIcon icon={faX} className="text-base sm:text-lg" />
          </button>
        </div>

        {/* Controls */}
        <div className="border-b p-3 sm:p-4 bg-gray-50 space-y-3">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <div className="relative flex-1 sm:max-w-sm">
              <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="text"
                placeholder="Search assigned products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => bulkUpdateStatus(true)}
                disabled={selectedCount === 0 || isBusy}
                className="px-3 py-2 text-sm rounded-md border border-green-300 text-green-700 bg-white hover:bg-green-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon icon={faToggleOn} className="mr-1" /> Activate
              </button>
              <button
                onClick={() => bulkUpdateStatus(false)}
                disabled={selectedCount === 0 || isBusy}
                className="px-3 py-2 text-sm rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon icon={faToggleOff} className="mr-1" /> Deactivate
              </button>
              <button
                onClick={() => setConfirmRemove(true)}
                disabled={selectedCount === 0 || isBusy}
                className="px-3 py-2 text-sm rounded-md border border-red-300 text-red-600 bg-white hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon icon={faTrash} className="mr-1" /> Remove
              </button>
            </div>
          </div>
          <div className="text-xs sm:text-sm text-gray-600">
            {selectedCount} of {filteredAssignments.length} selected
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <Loader />
          ) : filteredAssignments.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center px-4">
              <FontAwesomeIcon icon={faSearch} className="text-4xl text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No products assigned yet</h3>
              <p className="text-sm text-gray-600">Use "Assign Products" from the dashboard to add products to this hub.</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="sticky top-0 z-10 bg-[#fafafc] text-[#6c7184] border-b">
                <tr>
                  <th className="text-xs py-3 text-center w-10">
                    <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAllVisible} className="rounded" />
                  </th>
                  <th className="text-xs py-3 text-left px-2">Product</th>
                  <th className="text-xs py-3 text-center">SKU</th>
                  <th className="text-xs py-3 text-center">Price</th>
                  <th className="text-xs py-3 text-center">Stock</th>
                  <th className="text-xs py-3 text-center">Status</th>
                  <th className="text-xs py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="border-t">
                {currentItems.map(a => {
                  const isSelected = !!selectedIds[a.product_id];
                  const isEditing = !!editRows[a.product_id];
                  const edit = editRows[a.product_id] || {};
                  return (
                    <tr key={a.product_id} className={`text-sm border-b ${isSelected ? 'bg-blue-50' : ''}`}>
                      <td className="py-2 text-center">
                        <input type="checkbox" checked={isSelected} onChange={() => toggleRow(a.product_id)} className="rounded" />
                      </td>
                      <td className="py-2 px-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-8 h-8 bg-gray-200 rounded flex-shrink-0 flex items-center justify-center overflow-hidden">
                            {a.product_image ? (
                              <img src={`${imageUrl}${a.product_image}`} alt={a.product_name} className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-[8px] text-gray-400">N/A</span>
                            )}
                          </div>
                          <span className="truncate">{a.product_name}</span>
                        </div>
                      </td>
                      <td className="py-2 text-center">{a.sku || '—'}</td>
                      <td className="py-2 text-center">
                        {isEditing ? (
                          <input
                            type="number"
                            value={edit.price ?? ''}
                            onChange={(e) => updateEditField(a.product_id, 'price', e.target.value)}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-xs text-center"
                          />
                        ) : (
                          <span onClick={() => startEdit(a)} className="cursor-pointer hover:underline">₹{a.price}</span>
                        )}
                      </td>
                      <td className="py-2 text-center">
                        {isEditing ? (
                          <input
                            type="number"
                            value={edit.stock ?? ''}
                            onChange={(e) => updateEditField(a.product_id, 'stock', e.target.value)}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-xs text-center"
                          />
                        ) : (
                          <span onClick={() => startEdit(a)} className="cursor-pointer hover:underline">
                            {a.stock ?? a.stock === 0 ? a.stock : '—'}
                          </span>
                        )}
                      </td>
                      <td className="py-2 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${a.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {a.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-2 text-center">
                        {isEditing ? (
                          <button
                            onClick={() => saveRow(a)}
                            disabled={isBusy}
                            className="text-[#408dfb] hover:text-[#3579d9] text-xs flex items-center gap-1 mx-auto"
                          >
                            <FontAwesomeIcon icon={faSave} /> Save
                          </button>
                        ) : (
                          <button onClick={() => startEdit(a)} className="text-gray-500 hover:text-gray-700 text-xs">
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="border-t bg-white px-4 py-2">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onPreviousPage={handlePreviousPage}
            onNextPage={handleNextPage}
            itemName="assigned products"
          />
        </div>
      </div>

      {/* Remove confirmation */}
      {confirmRemove && (
        <div className="fixed inset-0 flex z-[60] items-center justify-center bg-gray-800 bg-opacity-50">
          <div className="bg-white rounded-md shadow-md w-[420px] p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-2">Remove {selectedCount} product(s)?</h3>
            <p className="text-sm text-gray-600 mb-5">
              This will unassign the selected products from {hub.hub_name}. This can't be undone from here.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmRemove(false)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={bulkRemove}
                disabled={isBusy}
                className="px-3 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HubAssignedProductsManager;
